import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { printingColors } from '../../lib/printing-theme';
import { PrintingProduct, extractUseCases, cleanDescription } from '../../lib/printing-products';
import { getPrintDetailSections } from '../../data/printing/catalogue';
import { useContactModal } from '../../contexts/ContactModalContext';
import PathSelector, { SubmissionPath } from './artwork-submission/PathSelector';
import SimpleUploader from './artwork-submission/SimpleUploader';
import HelpRequestForm from './artwork-submission/HelpRequestForm';
import ConsultationBooker from './artwork-submission/ConsultationBooker';
import type { ArtworkAnalysis } from '../../lib/artwork-types';

interface ProductDetailModalProps {
  product: PrintingProduct;
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'info' | 'path-select' | 'upload' | 'help' | 'consult' | 'details' | 'success';

interface SubmissionData {
  path: SubmissionPath | null;
  file?: File;
  analysis?: ArtworkAnalysis;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  isOpen,
  onClose,
}) => {
  const [step, setStep] = useState<ModalStep>('info');
  const [submissionData, setSubmissionData] = useState<SubmissionData>({ path: null });

  const imageUrl = product.images[0] || '/printing/placeholder.jpg';
  const useCases = extractUseCases(product);
  const { openContactModal } = useContactModal();

  const handleClose = useCallback(() => {
    setStep('info');
    setSubmissionData({ path: null });
    onClose();
  }, [onClose]);

  const handleContactClick = useCallback(() => {
    handleClose();
    openContactModal();
  }, [handleClose, openContactModal]);

  const handleGetStarted = () => {
    setStep('path-select');
  };

  const handleSelectPath = (path: SubmissionPath) => {
    setSubmissionData(prev => ({ ...prev, path }));
    if (path === 'ready') setStep('upload');
    else if (path === 'help') setStep('help');
    else if (path === 'consult') setStep('consult');
  };

  const handleUploadComplete = (file: File, analysis: ArtworkAnalysis) => {
    setSubmissionData(prev => ({ ...prev, file, analysis }));
    setStep('details');
  };

  const handleHelpComplete = () => {
    // HelpRequestForm already collected user details, go straight to success
    setStep('success');
  };

  const handleConsultComplete = () => {
    setStep('success');
  };

  const handleDetailsComplete = () => {
    setStep('success');
  };

  const handleBackToPath = () => {
    setStep('path-select');
  };

  if (!isOpen) return null;

  // Portal to <body>: the page wrapper is `isolate`, which would trap the modal below the fixed header
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="fixed inset-2 sm:inset-4 md:inset-8 lg:inset-12 z-[200] flex items-center justify-center pointer-events-none"
          >
            <div
              className="relative w-full max-w-3xl max-h-[85vh] sm:max-h-[82vh] overflow-hidden rounded-[15px] shadow-2xl pointer-events-auto flex flex-col"
              style={{ backgroundColor: step === 'info' ? '#ffffff' : printingColors.dark }}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-30 p-2.5 rounded-full transition-colors ${
                  step === 'info'
                    ? 'bg-black/5 hover:bg-black/10'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                <X className={`w-5 h-5 ${step === 'info' ? 'text-black' : 'text-white'}`} />
              </button>

              {/* Scrollable content */}
              <div 
                className={`flex-1 overflow-y-auto scrollbar-thin ${
                  step === 'info'
                    ? 'scrollbar-track-black/5 scrollbar-thumb-black/20 hover:scrollbar-thumb-black/30'
                    : 'scrollbar-track-white/5 scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30'
                }`} 
                style={{ 
                  scrollbarWidth: 'thin', 
                  scrollbarColor: step === 'info'
                    ? 'rgba(0,0,0,0.2) rgba(0,0,0,0.05)'
                    : 'rgba(255,255,255,0.2) rgba(255,255,255,0.05)' 
                }}
              >
                <AnimatePresence mode="wait">
                  {/* Step: Product Info */}
                  {step === 'info' && (
                    <motion.div
                      key="info"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="flex flex-col md:flex-row"
                    >
                      {/* Image - 40% on desktop with bounded height */}
                      <div className="md:w-[36%] flex-shrink-0 md:self-start">
                        <img
                          src={imageUrl}
                          alt={product.title}
                          className="w-full h-48 md:h-auto md:max-h-[320px] md:aspect-square object-cover"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-6 md:p-8">
                        <h2 className="hearns-font text-2xl md:text-3xl text-neutral-900 mb-2">
                          {product.title}
                        </h2>

                        <p className="neuzeit-light-font text-xs text-neutral-600 mb-4 leading-relaxed">
                          {cleanDescription(product.short_description)}
                        </p>

                        {/* Perfect for */}
                        <div className="mb-6">
                          <p className="smilecake-font text-base mb-2" style={{ color: printingColors.accent }}>
                            Perfect for...
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {useCases.map((useCase, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Check className="w-4 h-4" style={{ color: printingColors.accent }} />
                                <span className="neuzeit-font text-sm text-neutral-500">{useCase}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-black/10 mb-6" />

                        {/* Details Accordion */}
                        <div className="divide-y divide-black/10 border-b border-black/10 mb-6 md:mb-8">
                          {getPrintDetailSections(product).map((section) => (
                            <details key={section.title} className="group">
                              <summary className="flex items-center justify-between gap-6 py-3 cursor-pointer list-none font-montserrat font-bold text-sm md:text-base text-neutral-900 [&::-webkit-details-marker]:hidden">
                                {section.title}
                                <span 
                                  className="shrink-0 w-5 h-5 transition-transform duration-500 group-open:rotate-45"
                                  style={{ color: printingColors.accent }}
                                >
                                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M10 4v12M4 10h12" />
                                  </svg>
                                </span>
                              </summary>
                              <div className="pb-5">
                                <ul className="space-y-2">
                                  {section.items.map((item, idx) => (
                                    <li key={idx} className="neuzeit-font text-sm text-neutral-700 flex items-start gap-2">
                                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: printingColors.accent }} />
                                      {item.href ? (
                                        <a href={item.href} className="hover:underline" style={{ color: printingColors.accent }}>
                                          {item.text}
                                        </a>
                                      ) : (
                                        <span>{item.text}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </details>
                          ))}
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={handleGetStarted}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[10px] neuzeit-font font-semibold text-white transition-all hover:opacity-90"
                            style={{ backgroundColor: printingColors.accent }}
                          >
                            Request a Quote
                            <ArrowRight className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={handleContactClick}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[10px] neuzeit-font font-semibold transition-all hover:bg-[#78BE20]/10 border-2"
                            style={{ borderColor: printingColors.accent, color: printingColors.accent }}
                          >
                            Contact the Team
                          </button>
                        </div>

                        {/* Trust indicators */}
                        <div className="mt-8 pt-6 border-t border-black/10">
                          <div className="flex flex-wrap gap-6 text-sm text-neutral-700">
                            <div className="flex items-center gap-2">
                              <Check className="w-4 h-4" style={{ color: printingColors.accent }} />
                              <span className="neuzeit-font">In-house design team</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Check className="w-4 h-4" style={{ color: printingColors.accent }} />
                              <span className="neuzeit-font">25+ years of print experience</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Check className="w-4 h-4" style={{ color: printingColors.accent }} />
                              <span className="neuzeit-font">Premium paper stockists</span>
                            </div>
                          </div>
                          <p className="mt-4 neuzeit-font text-sm text-neutral-900 font-bold">
                            Got a deadline? <button type="button" onClick={handleContactClick} className="hover:underline" style={{ color: printingColors.accent }}>Contact our team</button> to check for express availability.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Step: Path Selection */}
                  {step === 'path-select' && (
                    <motion.div
                      key="path-select"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="p-8 md:p-12"
                    >
                      <button
                        onClick={() => setStep('info')}
                        className="flex items-center gap-2 mb-6 text-white/60 hover:text-white transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="neuzeit-font text-sm">Back to {product.title}</span>
                      </button>

                      <PathSelector
                        onSelectPath={handleSelectPath}
                        serviceName="printing"
                        recommendedPath="ready"
                      />
                    </motion.div>
                  )}

                  {/* Step: Upload */}
                  {step === 'upload' && (
                    <motion.div
                      key="upload"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="p-8 md:p-12"
                    >
                      <SimpleUploader
                        onBack={handleBackToPath}
                        onComplete={handleUploadComplete}
                        productTitle={product.title}
                        printDimensions={{ width: 148, height: 210 }}
                      />
                    </motion.div>
                  )}

                  {/* Step: Help Request */}
                  {step === 'help' && (
                    <motion.div
                      key="help"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="p-8 md:p-12"
                    >
                      <HelpRequestForm
                        onBack={handleBackToPath}
                        onComplete={handleHelpComplete}
                        productTitle={product.title}
                        serviceName="printing"
                      />
                    </motion.div>
                  )}

                  {/* Step: Consultation */}
                  {step === 'consult' && (
                    <motion.div
                      key="consult"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="p-8 md:p-12"
                    >
                      <ConsultationBooker
                        onBack={handleBackToPath}
                        onComplete={handleConsultComplete}
                        serviceName="printing"
                      />
                    </motion.div>
                  )}

                  {/* Step: Customer Details */}
                  {step === 'details' && (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="p-8 md:p-12"
                    >
                      <CustomerDetailsForm
                        productTitle={product.title}
                        onComplete={handleDetailsComplete}
                      />
                    </motion.div>
                  )}

                  {/* Step: Success */}
                  {step === 'success' && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-8 md:p-12"
                    >
                      <SubmissionSuccess
                        productTitle={product.title}
                        onBrowseMore={handleClose}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Customer Details Form Component
interface CustomerDetailsFormProps {
  productTitle: string;
  onComplete: () => void;
}

const CustomerDetailsForm: React.FC<CustomerDetailsFormProps> = ({
  productTitle,
  onComplete,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = name.trim() && email.trim() && email.includes('@');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setIsSubmitting(true);
    // Simulate submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    onComplete();
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="hearns-font text-3xl text-white mb-2">
          Almost there!
        </h2>
        <p className="neuzeit-light-font text-white/70">
          Just a few details so we can send your quote for {productTitle}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Name & Email */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="neuzeit-font text-sm text-white/70 block mb-2">
              Your name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all"
            />
          </div>
          <div>
            <label className="neuzeit-font text-sm text-white/70 block mb-2">
              Email address <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all"
            />
          </div>
        </div>

        {/* Phone & Company */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="neuzeit-font text-sm text-white/70 block mb-2">
              Phone <span className="text-white/40">(optional)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07123 456789"
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all"
            />
          </div>
          <div>
            <label className="neuzeit-font text-sm text-white/70 block mb-2">
              Company <span className="text-white/40">(optional)</span>
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Ltd"
              className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all"
            />
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="neuzeit-font text-sm text-white/70 block mb-2">
            Anything else we should know? <span className="text-white/40">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. specific quantities, deadline, special requirements..."
            rows={3}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 neuzeit-font focus:outline-none focus:border-white/50 transition-all resize-none"
          />
        </div>

        {/* Marketing opt-in */}
        <div className="mb-8">
          <label className="flex items-start gap-3 cursor-pointer">
            <div
              onClick={() => setMarketing(!marketing)}
              className={`w-5 h-5 mt-0.5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
                marketing ? 'border-transparent' : 'border-white/30'
              }`}
              style={marketing ? { backgroundColor: printingColors.accent } : {}}
            >
              {marketing && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="neuzeit-light-font text-sm text-white/60">
              Keep me updated on offers and new products
            </span>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-[10px] neuzeit-font font-semibold text-lg transition-all ${
            isValid && !isSubmitting
              ? 'text-white hover:opacity-90'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
          style={isValid && !isSubmitting ? { backgroundColor: printingColors.accent } : {}}
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Submit Request
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        {/* Privacy note */}
        <p className="mt-4 text-center neuzeit-light-font text-xs text-white/40">
          🔒 We'll never share your details with third parties
        </p>
      </form>
    </div>
  );
};

// Submission Success Component
interface SubmissionSuccessProps {
  productTitle: string;
  onBrowseMore: () => void;
}

const SubmissionSuccess: React.FC<SubmissionSuccessProps> = ({
  productTitle,
  onBrowseMore,
}) => {
  const steps = [
    'We review your artwork/requirements',
    'We\'ll be in touch with any questions',
    'You\'ll receive a detailed quote',
    'Once approved, we print and deliver',
  ];

  return (
    <div className="max-w-lg mx-auto text-center">
      {/* Checkmark */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.2 }}
        className="w-24 h-24 rounded-full mx-auto mb-8 flex items-center justify-center"
        style={{ backgroundColor: printingColors.accent }}
      >
        <Check className="w-12 h-12 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="hearns-font text-3xl text-white mb-2"
      >
        Request Sent!
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="smilecake-font text-xl mb-4"
        style={{ color: printingColors.accent }}
      >
        We're on it
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="neuzeit-light-font text-white/70 mb-8"
      >
        We've received your request for {productTitle}. Our team will review and get back to you within 24 hours.
      </motion.p>

      {/* Divider */}
      <div className="h-px bg-white/10 mb-8" />

      {/* What happens next */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-left mb-8"
      >
        <h3 className="neuzeit-font font-semibold text-white mb-4 text-center">
          What happens next
        </h3>
        <div className="space-y-3">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + i * 0.1 }}
              className="flex items-center gap-3"
            >
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold text-white"
                style={{ backgroundColor: printingColors.accent }}
              >
                {i + 1}
              </div>
              <span className="neuzeit-font text-sm text-white/80">{step}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Contact info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="p-4 rounded-[10px] bg-white/5 mb-8"
      >
        <p className="neuzeit-font text-sm text-white/60 mb-2">
          Questions? Get in touch:
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a
            href="tel:+441onal234567"
            className="neuzeit-font text-sm font-medium hover:underline"
            style={{ color: printingColors.accent }}
          >
            01234 567890
          </a>
          <a
            href="mailto:info@outpostcustom.co.uk"
            className="neuzeit-font text-sm font-medium hover:underline"
            style={{ color: printingColors.accent }}
          >
            info@outpostcustom.co.uk
          </a>
        </div>
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="flex flex-col sm:flex-row gap-3 justify-center"
      >
        <button
          onClick={onBrowseMore}
          className="px-6 py-3 rounded-[10px] neuzeit-font font-medium text-white transition-all hover:opacity-90"
          style={{ backgroundColor: printingColors.accent }}
        >
          Browse More Products
        </button>
        <a
          href="/"
          className="px-6 py-3 rounded-[10px] neuzeit-font font-medium text-white/70 bg-white/10 hover:bg-white/20 transition-all"
        >
          Back to Home
        </a>
      </motion.div>
    </div>
  );
};

export default ProductDetailModal;
